/**
 * 講義レビューの評価期間設定
 * 各学期の評価期間を定義
 */

export type ReviewPeriod = {
  academicYear: string;  // 例: '2026'
  semester: string;      // 例: '春', '秋'
  startDate: string;     // ISO日付形式: 'YYYY-MM-DD'
  gradeStartDate: string; // 成績入力開始日: 'YYYY-MM-DD'
  endDate: string;       // ISO日付形式: 'YYYY-MM-DD'（排他的）
};

// テスト用に強制的にレビューゲートを有効化するフラグ（本番リリース時は false にしてください）
export const FORCE_REVIEW_GATE = false;
// テスト用に強制的に成績ゲートを有効化するフラグ（本番リリース時は false にしてください）
export const FORCE_GRADE_GATE = false;

export const REVIEW_PERIODS: ReviewPeriod[] = [
  {
    academicYear: '2026',
    semester: '春',
    startDate: '2026-07-29',
    gradeStartDate: '2026-08-17',
    endDate: '2026-09-01',
  },
  // 今後の学期はここに追加
  // { academicYear: '2026', semester: '秋', startDate: '2026-01-XX' },
];

/**
 * JST 00:00 のタイムスタンプを取得（UTC+9）
 */
export const toJstTimestamp = (dateString: string): number => {
  const [year, month, day] = dateString.split('-').map(Number);
  // JST (UTC+9) の 00:00 は前日の UTC 15:00
  return Date.UTC(year, month - 1, day - 1, 15, 0, 0);
};

/**
 * 現在の学期を取得
 */
export const getCurrentSemester = (nowMs: number = Date.now()): { academicYear: string; semester: string } => {
  // 端末のタイムゾーンに依存せず、運用基準のJSTで学期を判定する。
  const nowInJst = new Date(nowMs + 9 * 60 * 60 * 1000);
  const year = nowInJst.getUTCFullYear();
  const month = nowInJst.getUTCMonth() + 1; // 1-12

  // 4月〜9月: 春学期, 10月〜3月: 秋学期
  if (month >= 4 && month <= 9) {
    return { academicYear: String(year), semester: '春' };
  } else {
    // 1月〜3月は前年度の秋学期
    const academicYear = month <= 3 ? String(year - 1) : String(year);
    return { academicYear, semester: '秋' };
  }
};

/**
 * 現在の学期に対応する評価期間を取得
 */
export const getReviewPeriod = (academicYear: string, semester: string): ReviewPeriod | null =>
  REVIEW_PERIODS.find(
    (period) => period.academicYear === academicYear && period.semester === semester
  ) || null;

export const getCurrentReviewPeriod = (nowMs: number = Date.now()): ReviewPeriod | null => {
  const current = getCurrentSemester(nowMs);
  return (
    getReviewPeriod(current.academicYear, current.semester)
  );
};

/**
 * 指定した年度・学期がレビュー受付中かどうか
 */
export const isReviewPeriodOpen = (
  academicYear: string,
  semester: string,
  nowMs: number = Date.now()
): boolean => {
  if (FORCE_REVIEW_GATE) return true;

  const period = getReviewPeriod(academicYear, semester);
  if (!period) return false;

  return nowMs >= toJstTimestamp(period.startDate) && nowMs < toJstTimestamp(period.endDate);
};

/**
 * 現在が評価期間内かどうか
 */
export const isInReviewPeriod = (nowMs: number = Date.now()): boolean => {
  const current = getCurrentSemester(nowMs);
  return isReviewPeriodOpen(current.academicYear, current.semester, nowMs);
};

/**
 * 現在が成績入力期間内かどうか
 */
export const isInGradePeriod = (nowMs: number = Date.now()): boolean => {
  // テスト用に強制ON可能（本番では false）
  if (FORCE_GRADE_GATE) return true;

  const period = getCurrentReviewPeriod(nowMs);
  if (!period) return false;

  return nowMs >= toJstTimestamp(period.gradeStartDate) && nowMs < toJstTimestamp(period.endDate);
};

/**
 * 学期の開始日を取得（新規ユーザー判定用）
 * 春学期: 4月1日, 秋学期: 10月1日
 */
export const getSemesterStartDate = (academicYear: string, semester: string): Date => {
  const year = parseInt(academicYear, 10);
  const month = semester === '春' ? 4 : 10;
  return new Date(year, month - 1, 1); // month is 0-indexed
};
